import { Link } from "react-router-dom";
import RevealOnScroll from "../components/RevealOnScroll";
import { ACCOUNTING_PRICING_SUMMARY } from "../constants/accountingPricing";
import { useBrand } from "../context/BrandContext";
import useParallaxScroll from "../hooks/useParallaxScroll";
import "./EllyannaBookingHome.css";

const focusRingClass =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-500";

const primaryButtonClass = `inline-flex min-h-11 w-full items-center justify-center rounded-xl bg-rose-500 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-600 sm:w-auto ${focusRingClass}`;
const secondaryButtonClass = `inline-flex min-h-11 w-full items-center justify-center rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-900 transition hover:border-slate-400 hover:bg-slate-50 sm:w-auto ${focusRingClass}`;
const cardClass =
  "min-w-0 rounded-[28px] border border-white/80 bg-white shadow-[0_24px_80px_rgba(47,53,102,0.12)]";
const smallCardClass =
  "rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_14px_45px_rgba(47,53,102,0.08)]";

const heroPreviewCards = [
  {
    title: "Today's Schedule",
    value: "12 appointments",
    body: "See the day at a glance.",
  },
  {
    title: "Client Profile",
    value: "Notes and history",
    body: "Keep client details easy to find.",
  },
  {
    title: "Daily Summary",
    value: "Sales, tickets, reports",
    body: "Review the day faster.",
  },
];

const audienceCards = [
  {
    eyebrow: "Salon Owners",
    title: "Manage your salon from one place.",
    body:
      "Set up your salon, manage staff, book clients, track sales, and keep daily work organized.",
    cta: "Register your salon",
    to: "/register",
  },
  {
    eyebrow: "Independent Professionals",
    title: "Run your own client business.",
    body:
      "Manage your own schedule, services, clients, sales, and business setup.",
    cta: "Create professional account",
    to: "/register?role=independent_professional",
  },
  {
    eyebrow: "Technicians & Staff",
    title: "Join your salon team.",
    body:
      "Join your salon with the Salon ID from your owner or manager.",
    cta: "Join an existing salon",
    to: "/register",
  },
];

const previewStripCards = [
  {
    title: "Schedule view",
    body: "Manage appointments, team calendars, and daily bookings.",
    image: "/ellyanna-booking/appointment-service.jpg",
    alt: "Independent professional providing a manicure service during an appointment.",
  },
  {
    title: "Client profile",
    body: "Keep service history, notes, preferences, and follow-up details together.",
    image: "/ellyanna-booking/client-consultation.jpg",
    alt: "Salon professional reviewing service preferences with a client.",
  },
  {
    title: "Daily sales",
    body: "Track tickets, services, products, and business summaries.",
    image: "/ellyanna-booking/salon-team.jpg",
    alt: "Two salon professionals smiling in a bright client care space.",
  },
];

const featureCards = [
  {
    icon: "calendar",
    title: "Appointments and schedule",
    body: "Book clients, plan the day, and keep the team organized.",
  },
  {
    icon: "client",
    title: "Clients and service history",
    body: "Keep notes, preferences, photos/details where available, and service records easy to find.",
  },
  {
    icon: "ticket",
    title: "Services, products, and tickets",
    body: "Manage your menu, product sales, tickets, and checkout.",
  },
  {
    icon: "chart",
    title: "Reports and business insight",
    body: "Review sales, taxes, client activity, and performance.",
  },
  {
    icon: "globe",
    title: "Online booking and client access",
    body: "Let clients request appointments online when enabled.",
  },
  {
    icon: "heart",
    title: "Marketing, rewards, and follow-up",
    body: "Bring clients back with reminders, rewards, reviews, and campaigns.",
  },
];

const includedFeatures = [
  "Appointment scheduling",
  "Client records",
  "Services and products",
  "Checkout and sales",
  "Reports and daily summaries",
  "Online booking setup",
  "Business setup guidance",
  "MyBoothShare and SalonBusinessCPA when needed",
];

const flowSteps = [
  {
    title: "Set up your business",
    body: "Add services, hours, staff, and business settings.",
  },
  {
    title: "Manage clients and appointments",
    body: "Keep bookings, client details, history, and daily activity organized.",
  },
  {
    title: "Review sales and performance",
    body:
      "Use tickets, reports, and summaries to see how the business is doing.",
  },
];

const pricingPlans = [
  {
    name: "Independent Professional",
    audience: "Solo beauty professional",
    price: "$39.99",
    range: "1 independent professional",
    description:
      "For solo beauty professionals managing their own clients, schedule, services, and sales.",
  },
  {
    name: "Salon Starter",
    audience: "Small salon team",
    price: "$79.99",
    range: "1 to 3 salon staff",
    description: "For small salons that need booking, client, staff, and sales tools in one place.",
  },
  {
    name: "Salon Growth",
    audience: "Growing salon",
    price: "$129.99",
    range: "4 to 6 salon staff",
    description: "For growing salons that need clearer scheduling, reporting, and follow-up.",
  },
  {
    name: "Salon Scale",
    audience: "Larger salon team",
    price: "$189.99",
    range: "7 to 10 salon staff",
    description: "For busy salons managing more service providers and daily activity.",
  },
];

function Badge({ children }) {
  return (
    <p className="inline-flex rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-rose-500">
      {children}
    </p>
  );
}

function Icon({ name }) {
  const shared = {
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.8",
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true">
      {name === "calendar" && (
        <>
          <rect x="3.5" y="5" width="17" height="15.5" rx="3" {...shared} />
          <path d="M8 3.5v4M16 3.5v4M4 10h16" {...shared} />
        </>
      )}
      {name === "client" && (
        <>
          <circle cx="9" cy="8" r="3" {...shared} />
          <path d="M4 19c.7-3.2 2.6-5 5-5s4.3 1.8 5 5" {...shared} />
          <path d="M15.5 9.5h4M17.5 7.5v4" {...shared} />
        </>
      )}
      {name === "ticket" && (
        <>
          <path d="M4 7.5h16v3a2 2 0 0 0 0 4v3H4v-3a2 2 0 0 0 0-4v-3z" {...shared} />
          <path d="M9 9.5h6M9 14.5h4" {...shared} />
        </>
      )}
      {name === "chart" && (
        <>
          <path d="M4 19h16" {...shared} />
          <path d="M7 16v-5M12 16V7M17 16v-8" {...shared} />
        </>
      )}
      {name === "globe" && (
        <>
          <circle cx="12" cy="12" r="8.5" {...shared} />
          <path d="M3.5 12h17M12 3.5c2.2 2.3 3.3 5.1 3.3 8.5s-1.1 6.2-3.3 8.5M12 3.5C9.8 5.8 8.7 8.6 8.7 12s1.1 6.2 3.3 8.5" {...shared} />
        </>
      )}
      {name === "heart" && (
        <path d="M12 20s-7.5-4.8-7.5-10.1A4.2 4.2 0 0 1 12 7.3a4.2 4.2 0 0 1 7.5 2.6C19.5 15.2 12 20 12 20z" {...shared} />
      )}
    </svg>
  );
}

function HeroPreviewCard({ title, value, body, delay }) {
  return (
    <div
      className="ellyanna-float-soft rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_14px_38px_rgba(47,53,102,0.08)]"
      style={{ animationDelay: `${delay}ms` }}
    >
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-rose-500">
        {title}
      </p>
      <p className="mt-1 text-sm font-semibold text-slate-950">{value}</p>
      <p className="mt-1 text-xs leading-5 text-slate-500">{body}</p>
    </div>
  );
}

function HeroVisual({ parallaxStyle }) {
  return (
    <div className={`${cardClass} relative overflow-hidden p-5 md:p-6`}>
      <div
        className="absolute -right-12 -top-12 h-36 w-36 rounded-full bg-rose-200/45 blur-2xl"
        aria-hidden="true"
        style={parallaxStyle}
      />
      <div className="relative aspect-[4/3] overflow-hidden rounded-[24px] border border-slate-200 bg-slate-950 md:min-h-[340px]">
        <img
          src="/ellyanna-booking/client-care.jpg"
          alt="Salon professional caring for a client's nails in a bright salon."
          className="h-full w-full object-cover opacity-75"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/35 via-slate-950/5 to-rose-500/20" />
        <div className="absolute bottom-3 left-3 right-3 rounded-2xl border border-white/25 bg-white/90 p-3 shadow-2xl backdrop-blur sm:bottom-5 sm:left-5 sm:right-5 sm:p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-rose-500">
                Online Booking
              </p>
              <p className="mt-1 text-sm font-semibold text-slate-950">
                Clients can request appointments
              </p>
            </div>
            <div className="rounded-full bg-slate-950 px-3 py-1 text-xs font-semibold text-white">
              Ready
            </div>
          </div>
        </div>
      </div>
      <div className="relative mt-4 grid gap-3 md:grid-cols-3">
        {heroPreviewCards.map((card, index) => (
          <HeroPreviewCard
            key={card.title}
            {...card}
            delay={index * 350}
          />
        ))}
      </div>
    </div>
  );
}

function AudienceCard({ eyebrow, title, body, cta, to, delay }) {
  return (
    <RevealOnScroll
      as="article"
      delay={delay}
      className={`${smallCardClass} flex h-full flex-col`}
    >
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-rose-500">
        {eyebrow}
      </p>
      <h3 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">
        {title}
      </h3>
      <p className="mt-3 flex-1 text-sm leading-6 text-slate-600">{body}</p>
      <div className="mt-6">
        <Link to={to} className={eyebrow === "Salon Owners" ? primaryButtonClass : secondaryButtonClass}>
          {cta}
        </Link>
      </div>
    </RevealOnScroll>
  );
}

function PreviewStripCard({ title, body, image, alt, delay }) {
  return (
    <RevealOnScroll
      as="article"
      delay={delay}
      className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_14px_45px_rgba(47,53,102,0.08)]"
    >
      <div className="aspect-[16/9] overflow-hidden bg-slate-100">
        <img src={image} alt={alt} className="h-full w-full object-cover" loading="lazy" />
      </div>
      <div className="p-5">
        <h3 className="text-lg font-semibold text-slate-950">{title}</h3>
        <p className="mt-2 text-sm leading-6 text-slate-600">{body}</p>
      </div>
    </RevealOnScroll>
  );
}

function FeatureCard({ icon, title, body, delay }) {
  return (
    <RevealOnScroll
      as="article"
      delay={delay}
      className="rounded-2xl border border-slate-200 bg-slate-50 p-5"
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-50 text-rose-500">
        <Icon name={icon} />
      </div>
      <h3 className="mt-4 text-lg font-semibold text-slate-950">{title}</h3>
      <p className="mt-2 text-sm leading-6 text-slate-600">{body}</p>
    </RevealOnScroll>
  );
}

function FlowStep({ title, body, index }) {
  return (
    <RevealOnScroll as="article" delay={index * 120} className="relative rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#2f3566] text-sm font-semibold text-white">
        {index + 1}
      </div>
      <h3 className="mt-4 text-xl font-semibold text-slate-950">{title}</h3>
      <p className="mt-2 text-sm leading-6 text-slate-600">{body}</p>
    </RevealOnScroll>
  );
}

function PricingPlanCard({ name, audience, price, range, description, delay }) {
  const isIndependentPlan = name === "Independent Professional";
  return (
    <RevealOnScroll
      as="article"
      delay={delay}
      className={`flex h-full flex-col rounded-2xl border bg-white p-5 shadow-[0_14px_45px_rgba(47,53,102,0.08)] ${
        isIndependentPlan ? "border-rose-200 ring-1 ring-rose-100" : "border-slate-200"
      }`}
    >
      {isIndependentPlan ? (
        <p className="mb-3 inline-flex w-fit rounded-full bg-rose-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-rose-500">
          Great for new pros
        </p>
      ) : null}
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-rose-500">
        {audience}
      </p>
      <h3 className="mt-3 text-xl font-semibold tracking-tight text-slate-950">
        {name}
      </h3>
      <p className="mt-2 text-sm font-semibold text-slate-500">{range}</p>
      <div className="mt-5 flex items-end gap-2">
        <span className="text-4xl font-semibold tracking-tight text-slate-950">
          {price}
        </span>
        <span className="pb-1 text-sm font-medium text-slate-500">/ month</span>
      </div>
      <p className="mt-4 flex-1 text-sm leading-6 text-slate-600">{description}</p>
    </RevealOnScroll>
  );
}

function ProductContrastCard({ title, label, items, tone }) {
  const isRose = tone === "rose";
  return (
    <div className={`rounded-2xl border p-5 ${isRose ? "border-rose-200 bg-rose-50/70" : "border-slate-200 bg-slate-50"}`}>
      <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${isRose ? "text-rose-500" : "text-slate-500"}`}>
        {label}
      </p>
      <h3 className="mt-2 text-xl font-semibold text-slate-950">{title}</h3>
      <ul className="mt-4 space-y-2 text-sm leading-6 text-slate-600">
        {items.map((item) => (
          <li key={item} className="flex gap-2">
            <span className={`mt-2 h-2 w-2 shrink-0 rounded-full ${isRose ? "bg-rose-400" : "bg-slate-400"}`} aria-hidden="true" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function EllyannaBookingHome() {
  const runtimeBrand = useBrand();
  const parallax = useParallaxScroll();
  const marketplaceHref = runtimeBrand.marketplaceHomeUrl || "https://myboothshare.com/";
  const accountingHref = runtimeBrand.accountingHostName
    ? `https://${runtimeBrand.accountingHostName}/`
    : "https://salonbusinesscpa.com/";

  return (
    <div className="ellyanna-home-surface min-h-full bg-[radial-gradient(circle_at_top_left,rgba(255,236,239,0.92),transparent_34%),linear-gradient(135deg,#fff7f8_0%,#f4f7ff_100%)] px-4 py-8 md:px-6">
      <div className="ellyanna-parallax-layer" aria-hidden="true">
        <div className="ellyanna-blob ellyanna-blob-rose" style={parallax.hero} />
        <div className="ellyanna-blob ellyanna-blob-indigo" style={parallax.mid} />
      </div>
      <div className="relative mx-auto max-w-6xl space-y-7 pb-2">
        <section
          className="grid gap-5 lg:grid-cols-[1.02fr,0.98fr] lg:items-stretch"
          aria-labelledby="ellyanna-booking-home-title"
        >
          <RevealOnScroll className={`${cardClass} p-6 md:p-8`}>
            <Badge>Ellyanna Booking</Badge>
            <h1
              id="ellyanna-booking-home-title"
              className="mt-6 max-w-3xl break-words text-4xl font-semibold tracking-tight text-slate-950 md:text-5xl md:leading-tight"
            >
              Salon booking and management made simpler.
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-7 text-slate-600 md:text-lg">
              Ellyanna helps salon owners and beauty professionals manage
              appointments, clients, staff, services, sales, and daily operations
              in one place.
            </p>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Link to="/register" className={primaryButtonClass}>
                Create your account
              </Link>
              <Link to="/login" className={secondaryButtonClass}>
                Sign in
              </Link>
            </div>
            <div className="mt-5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold leading-6 text-rose-700">
              Start with a 30-day free trial. No credit card required.
            </div>
            <p className="mt-4 text-sm leading-6 text-slate-500">
              Already part of a salon team? Use the Salon ID from your salon
              owner or manager.
            </p>
          </RevealOnScroll>

          <RevealOnScroll delay={120} className="min-w-0">
            <HeroVisual parallaxStyle={parallax.mid} />
          </RevealOnScroll>
        </section>

        <section
          id="who-it-helps"
          className="px-1 py-3 md:px-2"
          aria-labelledby="paths-heading"
        >
          <RevealOnScroll className="max-w-3xl">
            <h2
              id="paths-heading"
              className="break-words text-3xl font-semibold tracking-tight text-slate-950 md:text-4xl"
            >
              Choose how you run your beauty business.
            </h2>
            <p className="mt-3 text-base leading-7 text-slate-600">
              Whether you are opening a salon, growing your team, working
              independently, or joining a salon, Ellyanna gives you a clear place
              to start.
            </p>
          </RevealOnScroll>
          <div className="mt-6 grid gap-5 lg:grid-cols-3">
            {audienceCards.map((card, index) => (
              <AudienceCard key={card.eyebrow} {...card} delay={index * 120} />
            ))}
          </div>
        </section>

        <section className="grid gap-4 md:grid-cols-3" aria-label="Ellyanna Booking preview cards">
          {previewStripCards.map((card, index) => (
            <PreviewStripCard key={card.title} {...card} delay={index * 120} />
          ))}
        </section>

        <section
          id="features"
          className="px-1 py-3 md:px-2"
          aria-labelledby="features-heading"
        >
          <RevealOnScroll>
            <h2
              id="features-heading"
              className="break-words text-3xl font-semibold tracking-tight text-slate-950 md:text-4xl"
            >
              Manage the daily work of your salon.
            </h2>
          </RevealOnScroll>
          <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {featureCards.map((card, index) => (
              <FeatureCard key={card.title} {...card} delay={index * 80} />
            ))}
          </div>
        </section>

        <section
          id="included"
          className={`${cardClass} px-6 py-7 md:px-8`}
          aria-labelledby="included-heading"
        >
          <RevealOnScroll className="grid gap-6 lg:grid-cols-[0.9fr,1.1fr] lg:items-start">
            <div>
              <Badge>What is included</Badge>
              <h2
                id="included-heading"
                className="mt-4 break-words text-3xl font-semibold tracking-tight text-slate-950 md:text-4xl"
              >
                Core salon tools included from the start.
              </h2>
              <p className="mt-3 text-base leading-7 text-slate-600">
                Ellyanna gives new beauty professionals and growing salons the
                daily tools they need without making setup feel complicated.
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {includedFeatures.map((feature) => (
                <div
                  key={feature}
                  className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700"
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-xl bg-rose-50 text-rose-500">
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden="true">
                      <path
                        d="m5 12.5 4.2 4.2L19 7"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </section>

        <section className={`${cardClass} px-6 py-7 md:px-8`} aria-labelledby="flow-heading">
          <RevealOnScroll className="max-w-3xl">
            <h2
              id="flow-heading"
              className="break-words text-3xl font-semibold tracking-tight text-slate-950 md:text-4xl"
            >
              A simpler way to run the day.
            </h2>
            <p className="mt-3 text-base leading-7 text-slate-600">
              Set up your business, manage clients and bookings, then review
              sales and performance.
            </p>
          </RevealOnScroll>
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {flowSteps.map((step, index) => (
              <FlowStep key={step.title} {...step} index={index} />
            ))}
          </div>
        </section>

        <section className={`${cardClass} px-6 py-7 md:px-8`} aria-labelledby="myboothshare-cross-link">
          <div className="grid gap-6 lg:grid-cols-[1fr,1.1fr] lg:items-center">
            <RevealOnScroll>
              <h2
                id="myboothshare-cross-link"
                className="break-words text-3xl font-semibold tracking-tight text-slate-950 md:text-4xl"
              >
                Need workspace rentals too?
              </h2>
              <p className="mt-3 max-w-3xl text-base leading-7 text-slate-600">
                MyBoothShare is the workspace rental side of Ellyanna. Use it to
                list chairs, rooms, or stations, or to find a professional
                workspace.
              </p>
              <a href={marketplaceHref} className={`${secondaryButtonClass} mt-6`}>
                Open MyBoothShare
              </a>
            </RevealOnScroll>
            <RevealOnScroll delay={120} className="grid gap-4 md:grid-cols-2">
              <ProductContrastCard
                label="Ellyanna Booking"
                title="Salon booking and management"
                tone="rose"
                items={["Appointments", "Clients and services", "Staff, sales, and reports"]}
              />
              <ProductContrastCard
                label="MyBoothShare"
                title="Workspace rentals"
                items={["Workspace listings", "Rental requests", "Chairs, rooms, and stations"]}
              />
            </RevealOnScroll>
          </div>
        </section>

        <section className={`${cardClass} px-6 py-7 md:px-8`} aria-labelledby="salonbusinesscpa-cross-link">
          <div className="grid gap-6 lg:grid-cols-[1fr,1.1fr] lg:items-center">
            <RevealOnScroll>
              <h2
                id="salonbusinesscpa-cross-link"
                className="break-words text-3xl font-semibold tracking-tight text-slate-950 md:text-4xl"
              >
                Need accounting and tax help too?
              </h2>
              <p className="mt-3 max-w-3xl text-base leading-7 text-slate-600">
                SalonBusinessCPA helps Canadian beauty businesses organize tax,
                GST/HST, payroll, bookkeeping, owner pay, and records. Use it
                when you need support beyond daily salon management.
              </p>
              <a href={accountingHref} className={`${secondaryButtonClass} mt-6`}>
                Open SalonBusinessCPA
              </a>
            </RevealOnScroll>
            <RevealOnScroll delay={120} className="grid gap-4 md:grid-cols-2">
              <ProductContrastCard
                label="Ellyanna Booking"
                title="Daily salon management"
                tone="rose"
                items={["Appointments", "Clients and staff", "Sales and reports"]}
              />
              <ProductContrastCard
                label="SalonBusinessCPA"
                title="Accounting and tax records"
                items={["Bookkeeping", "GST/HST and payroll", "Tax-ready records"]}
              />
            </RevealOnScroll>
          </div>
        </section>

        <section
          id="pricing"
          className={`${cardClass} px-6 py-7 md:px-8`}
          aria-labelledby="pricing-heading"
        >
          <RevealOnScroll
            id="free-trial"
            className="grid gap-5 lg:grid-cols-[1fr,auto] lg:items-end"
          >
            <div className="max-w-3xl">
              <Badge>Pricing</Badge>
              <h2
                id="pricing-heading"
                className="mt-4 break-words text-3xl font-semibold tracking-tight text-slate-950 md:text-4xl"
              >
                Simple monthly plans for beauty businesses.
              </h2>
              <p className="mt-3 text-base leading-7 text-slate-600">
                Start with a 30-day free trial. No credit card required. Choose
                the plan that fits your business when you are ready.
              </p>
              <p className="mt-3 text-base leading-7 text-slate-600">
                Every plan includes the core Ellyanna Booking system: appointments,
                clients, services, sales, setup, reports, online booking tools, and
                daily management features.
              </p>
            </div>
            <Link to="/register" className={primaryButtonClass}>
              Start free trial
            </Link>
          </RevealOnScroll>
          <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {pricingPlans.map((plan, index) => (
              <PricingPlanCard key={plan.name} {...plan} delay={index * 90} />
            ))}
          </div>
          <RevealOnScroll className="mt-5 rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm font-semibold leading-6 text-rose-700">
            No credit card required during the trial. Choose the plan that fits
            your business when you are ready.
          </RevealOnScroll>
          <RevealOnScroll className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4 text-sm leading-6 text-slate-600">
            <span className="font-semibold text-slate-900">
              SalonBusinessCPA packages are available separately.
            </span>{" "}
            Independent professionals start at $39/month, incorporated support
            starts at $199/month, and salon owner pricing scales with staff count.
            Staff access is included with the salon owner package.
            <span className="sr-only">{ACCOUNTING_PRICING_SUMMARY.join(" ")}</span>
          </RevealOnScroll>
        </section>

        <section className="relative overflow-hidden rounded-[28px] border border-white/80 bg-gradient-to-br from-white via-rose-50 to-indigo-50 px-6 py-8 shadow-[0_24px_80px_rgba(47,53,102,0.12)] md:px-8" aria-labelledby="final-cta-title">
          <RevealOnScroll className="grid gap-5 lg:grid-cols-[1fr,auto] lg:items-center">
            <div>
              <h2
                id="final-cta-title"
                className="break-words text-3xl font-semibold tracking-tight text-slate-950 md:text-4xl"
              >
                Start with the account that fits your business.
              </h2>
              <p className="mt-3 text-base leading-7 text-slate-600">
                Create your Ellyanna account, then follow the setup steps for
                your salon or independent beauty business.
              </p>
            </div>
            <div className="flex flex-col items-start gap-3 lg:items-end">
              <div className="rounded-2xl border border-white/80 bg-white/80 px-4 py-3 text-sm font-semibold text-slate-700 shadow-lg ellyanna-float-soft-slow" aria-hidden="true">
                Bookings, clients, sales
              </div>
              <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:flex-wrap lg:justify-end">
                <Link to="/register" className={primaryButtonClass}>
                  Create account
                </Link>
                <Link to="/login" className={secondaryButtonClass}>
                  Sign in
                </Link>
              </div>
            </div>
          </RevealOnScroll>
        </section>
      </div>
    </div>
  );
}
